// src/pages/BlogDetails.jsx
import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import { getAllBlogPosts } from "../services/blogService";

export default function BlogDetails() {
    const { id, lang } = useParams();
    const currentLang = lang || 'en';
    const navigate = useNavigate();

    const allPosts = useMemo(() => getAllBlogPosts(), []);
    const post = useMemo(() => {
        return allPosts.find((p) => p.id === parseInt(id));
    }, [allPosts, id]);

    // Related Posts
    const relatedPosts = useMemo(() => {
        if (!post) return [];
        return allPosts
            .filter((p) => p.category === post.category && p.id !== post.id)
            .slice(0, 3);
    }, [allPosts, post]);

    if (!post) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Article Not Found</h2>
                    <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate(`/${currentLang}/blog`)}
                        className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                    >
                        Back to Blog
                    </button>
                </div>
            </div>
        );
    }

    // دالة لتحويل الـ content array إلى JSX
    const renderContent = (items) => {
        return items.map((item, index) => {
            switch (item.type) {
                case "heading":
                    const HeadingTag = `h${item.level}`;
                    return (
                        <HeadingTag key={index} className="text-3xl md:text-4xl font-bold text-gray-900 mt-12 mb-6">
                            {item.text}
                        </HeadingTag>
                    );

                case "paragraph":
                    return (
                        <p key={index} className="text-lg text-gray-700 leading-relaxed mb-6">
                            {item.text}
                        </p>
                    );

                case "list":
                    return item.ordered ? (
                        <ol key={index} className="list-decimal list-inside space-y-3 mb-8 ml-6">
                            {item.items.map((li, i) => (
                                <li key={i} className="text-lg text-gray-700">{li}</li>
                            ))}
                        </ol>
                    ) : (
                        <ul key={index} className="list-disc list-inside space-y-3 mb-8 ml-6">
                            {item.items.map((li, i) => (
                                <li key={i} className="text-lg text-gray-700">{li}</li>
                            ))}
                        </ul>
                    );

                case "steps":
                    return (
                        <div key={index} className="my-12 space-y-8">
                            {item.items.map((step, i) => (
                                <div key={i} className="flex gap-6">
                                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                                        {i + 1}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h4>
                                        <p className="text-gray-700 text-lg">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    );

                case "divider":
                    return <hr key={index} className="my-16 border-gray-200" />;

                case "callout":
                    const variantStyles = {
                        info: "bg-blue-50 border-blue-200 text-blue-900",
                        warning: "bg-amber-50 border-amber-200 text-amber-900",
                    };
                    return (
                        <div
                            key={index}
                            className={`p-6 rounded-2xl border-l-4 ${variantStyles[item.variant || "info"]} mb-10`}
                        >
                            <h4 className="font-bold text-xl mb-3">{item.title}</h4>
                            <p className="text-lg">{item.text}</p>
                        </div>
                    );

                case "quote":
                    return (
                        <figure key={index} className="my-12 border-l-4 border-blue-600 pl-8 italic">
                            <blockquote className="text-2xl text-gray-800 leading-relaxed">
                                "{item.text}"
                            </blockquote>
                            {item.cite && (
                                <figcaption className="mt-4 text-right text-lg font-medium text-gray-600">
                                    — {item.cite}
                                </figcaption>
                            )}
                        </figure>
                    );

                case "cta":
                    return (
                        <div key={index} className="my-16 text-center">
                            <LanguageLink
                                to={item.href || "/contact"}
                                className="inline-block px-10 py-5 bg-blue-600 text-white text-xl font-bold rounded-full hover:bg-blue-700 transition shadow-lg hover:shadow-xl"
                            >
                                {item.text}
                            </LanguageLink>
                        </div>
                    );

                default:
                    return null;
            }
        });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* نفس الـ Hero Section المعتمد بالضبط */}
            <section className="relative isolate overflow-hidden text-white py-24 md:py-32">
                <div className="absolute inset-0 -z-30 animate-[gradientMove_16s_ease_infinite] bg-[linear-gradient(120deg,#1e3a8a_0%,#1d4ed8_50%,#2563eb_100%)] bg-[length:200%_200%] opacity-95" />

                <div className="pointer-events-none absolute inset-0 -z-20">
                    <span className="ball absolute left-[6%] top-[18%]" />
                    <span className="ball absolute right-[10%] top-[28%] delay-300" />
                    <span className="ball absolute left-[15%] bottom-[18%] delay-700" />
                    <span className="ball-sm absolute right-[22%] bottom-[26%] delay-500" />
                    <span className="ball-sm absolute left-[28%] top-[36%] delay-1000" />
                </div>

                <div
                    aria-hidden
                    className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-white/10 blur-3xl -z-10"
                    style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
                />
                <div
                    aria-hidden
                    className="absolute -bottom-32 -right-32 h-[34rem] w-[34rem] rounded-full bg-white/10 blur-3xl -z-10"
                    style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
                />

                <div className="relative max-w-6xl mx-auto px-6 sm:px-8 text-center">
                    <motion.div
                        className="inline-flex items-center gap-2 mb-6 rounded-full border border-white/20 bg-white/10 px-6 py-2 backdrop-blur-md"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-[pingSlow_2.4s_linear_infinite]" />
                        <span className="text-sm font-semibold tracking-wide text-white/90">
                            Immigration Blog
                        </span>
                    </motion.div>

                    <motion.h1
                        className="text-5xl md:text-6xl font-black tracking-tight leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,.35)]"
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05, duration: 0.65 }}
                        style={{
                            background: "linear-gradient(180deg, #ffffff 0%, #e2e8f0 60%, rgba(255,255,255,.85) 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        {post.title}
                    </motion.h1>

                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 0.25, duration: 0.7 }}
                        className="relative mx-auto mt-4 h-[3px] w-48 origin-center rounded-full bg-gradient-to-r from-cyan-300 via-white to-blue-300 shadow-[0_0_18px_rgba(255,255,255,.6)] overflow-hidden"
                    >
                        <div className="absolute inset-0 shimmer opacity-80" />
                        <div className="absolute -inset-1 bg-white/40 blur-md glowPulse rounded-full" />
                    </motion.div>

                    <motion.div
                        className="mt-8 flex flex-wrap justify-center items-center gap-6 text-sm"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        <div className="flex items-center gap-2">
                            <Calendar size={18} />
                            <span>{post.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock size={18} />
                            <span>{post.readTime}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <User size={18} />
                            <span>{post.author}</span>
                        </div>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none -z-10">
                    <svg viewBox="0 0 1440 240" className="w-full h-[110px] md:h-[150px]" preserveAspectRatio="none">
                        <path
                            className="animate-[waveDrift_14s_ease_in_out_infinite]"
                            fill="rgba(255,255,255,0.20)"
                            d="M0,140 C180,160 360,120 540,140 C720,160 900,200 1080,170 C1260,140 1350,120 1440,130 L1440,240 L0,240 Z"
                        />
                        <path
                            className="animate-[waveDrift_12s_ease_in_out_infinite_reverse]"
                            fill="rgba(255,255,255,0.28)"
                            d="M0,150 C200,130 420,170 640,155 C860,140 1000,150 1220,160 C1330,165 1385,165 1440,166 L1440,240 L0,240 Z"
                        />
                        <path
                            className="animate-[waveDrift_10s_ease_in_out_infinite]"
                            fill="rgba(255,255,255,0.36)"
                            d="M0,165 C240,170 420,160 620,168 C820,176 1040,154 1240,162 C1340,166 1390,166 1440,166 L1440,240 L0,240 Z"
                        />
                    </svg>
                </div>

                <style jsx>{`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes waveDrift {
            0% { transform: translateX(0px) translateY(0px); }
            50% { transform: translateX(-20px) translateY(6px); }
            100% { transform: translateX(0px) translateY(0px); }
          }
          @keyframes pingSlow {
            0% { transform: scale(0.85); opacity: 1; }
            70% { transform: scale(1.25); opacity: 0; }
            100% { transform: scale(0.85); opacity: 1; }
          }
          @keyframes shimmer {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(120%); }
          }
          @keyframes glowPulse {
            0%,100% { opacity: .45; }
            50% { opacity: .85; }
          }
          .shimmer {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,.95), transparent);
            animation: shimmer 2.2s ease-in-out infinite;
          }
          .glowPulse {
            animation: glowPulse 2.8s ease-in-out infinite;
          }
          @keyframes floatA {
            0% { transform: translateY(0) translateX(0) scale(1); }
            50% { transform: translateY(-16px) translateX(10px) scale(1.03); }
            100% { transform: translateY(0) translateX(0) scale(1); }
          }
          @keyframes floatB {
            0% { transform: translateY(0) translateX(0) scale(1); }
            50% { transform: translateY(14px) translateX(-12px) scale(0.98); }
            100% { transform: translateY(0) translateX(0) scale(1); }
          }
          .ball {
            width: 220px; height: 220px; border-radius: 9999px;
            background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.55), rgba(255,255,255,.12) 60%, rgba(255,255,255,0) 70%);
            filter: blur(2px);
            mix-blend-mode: screen;
            animation: floatA 9s ease-in-out infinite;
          }
          .ball-sm {
            width: 130px; height: 130px; border-radius: 9999px;
            background: radial-gradient(circle at 30% 30%, rgba(255,255,255,.45), rgba(255,255,255,.1) 60%, rgba(255,255,255,0) 70%);
            filter: blur(1px);
            mix-blend-mode: screen;
            animation: floatB 11s ease-in-out infinite;
          }
          .delay-300 { animation-delay: .3s !important; }
          .delay-500 { animation-delay: .5s !important; }
          .delay-700 { animation-delay: .7s !important; }
          .delay-1000 { animation-delay: 1s !important; }
        `}</style>
            </section>

            {/* زر الرجوع */}
            <div className="max-w-6xl mx-auto px-6 py-8">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition"
                >
                    <ArrowLeft size={20} />
                    Back to Blog
                </button>
            </div>

            {/* المحتوى الرئيسي */}
            <section className="py-12 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <ScrollAnimation direction="up">
                        <article className="prose prose-lg max-w-none">
                            {renderContent(post.content)}
                        </article>
                    </ScrollAnimation>
                </div>
            </section>

            {/* Related Articles */}
            {relatedPosts.length > 0 && (
                <section className="py-16 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <ScrollAnimation direction="up">
                            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                                Related Articles
                            </h2>
                        </ScrollAnimation>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {relatedPosts.map((related, index) => (
                                <ScrollAnimation key={related.id} direction="up" delay={index * 0.1}>
                                    <LanguageLink to={`/blog/${related.id}`}>
                                        <motion.div
                                            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                                            whileHover={{ scale: 1.02, y: -5 }}
                                        >
                                            <div className="h-48 relative overflow-hidden">
                                                <img
                                                    src={related.image}
                                                    alt={related.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="p-6">
                                                <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition">
                                                    {related.title}
                                                </h3>
                                                <p className="text-gray-600 line-clamp-2 mb-4">{related.excerpt}</p>
                                                <div className="text-sm text-gray-500">
                                                    <span>{related.author}</span> • <span>{related.readTime}</span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </LanguageLink>
                                </ScrollAnimation>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}